import React, { PropTypes, PureComponent } from 'react';
import { NOTIFICATION_CONTEXT_TYPES } from '../../../NotificationSystemProvider';
import { BracketGame, BracketGenerator } from 'react-tournament-bracket';
import EmptyState from '../../../common/components/EmptyState';
import TourneyFilterDataProvider from '../../models/TourneyFilterDataProvider';
import AdminGameModal from '../../../components/modals/AdminGameModal';
import { Button, LoadingWrapper } from 'react-fast-ui';
import SeedBracketWizard from '../../../components/wizards/SeedBracketWizard';
import SaveCancelModal from '../../../components/modals/SaveCancelModal';
import EnterScoreModal from '../../../components/modals/EnterScoreModal';
import Toggle from 'react-toggle';
import LabelBracketModal from '../../../components/modals/LabelBracketModal';
import _ from 'underscore';
import queuePromises from '../../../util/queuePromises';
import moment from 'moment-timezone';
import AdminFeature from '../../../common/access/AdminFeature';
import AdminBracketActionModal from '../../../components/modals/AdminBracketActionModal';

const SeedModal = ({ open, bracketId, homeOnTop, ...rest }) => (
  <SaveCancelModal title="Seed Bracket" open={open} size="lg" {...rest}>
    {open ? <SeedBracketWizard homeOnTop={homeOnTop} bracketId={bracketId}/> : null}
  </SaveCancelModal>
);

const getBracketGames = (game, visited = {}, round = 0) => {
  if (!game) {
    return [];
  }

  if (visited[ game.id ]) {
    return [];
  }

  visited[ game.id ] = true;

  return [ { game, round } ]
    .concat(
      _.flatten(
        _.map(
          game.sides,
          ({ seed }) => {
            if (!seed) {
              return [];
            }

            const { sourceGame, rank } = seed;

            if (!sourceGame || rank !== 1) {
              return [];
            }
            // must be in the same bracket
            if (sourceGame.gameGroup.id !== game.gameGroup.id) {
              return [];
            }

            return getBracketGames(sourceGame, visited, round + 1);
          }
        ),
        true
      )
    );
};

class AdminBracketGenerator extends PureComponent {
  static propTypes = {
    games: PropTypes.arrayOf(PropTypes.object).isRequired,
    homeOnTop: PropTypes.bool,
    onBracketEdit: PropTypes.func.isRequired,
    onClickGame: PropTypes.func.isRequired,
    hoveredTeamId: PropTypes.string.isRequired,
    onHoveredTeamIdChange: PropTypes.func.isRequired,
  };

  render() {
    const { homeOnTop, games, onBracketEdit, onClickGame, hoveredTeamId, onHoveredTeamIdChange } = this.props;

    return (
      <BracketGenerator
        homeOnTop={homeOnTop}
        games={games}
        gameDimensions={{ width: 200, height: 100 }}
        titleComponent={
          ({ game, height }) => (
            <h3 style={{ textAlign: 'center' }}>
              {game.bracketLabel || game.name}
              {
                height > 1 ?
                  (
                    <span className="text-muted"> ({height} Rounds)</span>
                  ) : null
              }
              {
                onClickGame && height > 1 &&
                <Button
                  borderless icon="pencil" size="sm"
                  onClick={() => onBracketEdit({ game, height })}/>
              }
            </h3>
          )
        }
        GameComponent={
          props => (
            <BracketGame
              {...props}
              onClick={onClickGame ? () => onClickGame(props.game) : null}
              style={{ cursor: onClickGame ? 'pointer' : 'default' }}
              hoveredTeamId={hoveredTeamId}
              onHoveredTeamIdChange={_.debounce(hoveredTeamId => onHoveredTeamIdChange(hoveredTeamId), 100)}
              topText={
                ({ scheduled, gameGroup: { division: { tourney: { timezone } } } }) =>
                  moment.tz(scheduled, timezone).format('l LT z')
              }
              bottomText={
                ({ name, bracketLabel, court: { name: courtName, venue: { nickname: venueNickname } } }) =>
                  ([
                    <tspan>{_.compact([ name, bracketLabel ]).join(' - ')}</tspan>,
                    <tspan x="100" dy="1em">{_.compact([ courtName, venueNickname ]).join(' in ')}</tspan>
                  ])
              }
            />
          )
        }/>
    );
  }
}

export default class AdminBracketPage extends PureComponent {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  static propTypes = {
    bracket: PropTypes.object,
    location: PropTypes.shape({ query: PropTypes.shape({ homeOnTop: PropTypes.string }) }).isRequired,
    params: PropTypes.shape({
      tourneyId: PropTypes.string,
      divisionId: PropTypes.string,
      bracketId: PropTypes.string
    }).isRequired
  };

  static defaultProps = {};

  state = {
    games: null,
    seeding: false,
    promise: null,
    filterData: null,
    editingGame: null,
    editingScore: null,
    hoveredTeamId: null,
    actionModal: false,
  };

  componentDidMount() {
    this._filterDataProvider = new TourneyFilterDataProvider(this.context.dao);
    this.refreshData();
  }

  componentWillReceiveProps({ bracket }) {
    if (bracket.id !== this.props.bracket.id) {
      this.fetchGames(bracket);
    }
  }

  fetchGames = ({ id: bracketId, division: { tourney } }, refresh = false) => {
    const { dao, onError } = this.context;
    const { promise } = this.state;

    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      games: refresh ? this.state.games : null,
      promise: Promise.all([
        dao.games.getAll({ gameGroupId: bracketId, sort: 'A|name', type: 'Bracket' }),
        this._filterDataProvider.getFilterData(tourney)
      ])
        .then(([ games, filterData ]) => {
          this.setState({ games, filterData });
        })
        .catch(onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  refreshData = () => this.fetchGames(this.props.bracket, true);

  handleEditGame = editingGame => {
    if (this.state.editingGame) {
      this.setState({ editingGame });
    } else {
      this.setState({ editingGame, actionModal: true });
    }
  };
  cancelEdit = () => this.setState({ editingGame: false, actionModal: false, editingScore: false });
  handleSave = () => {
    const { editingGame } = this.state;
    const { dao, onSuccess, onError } = this.context;

    this.setState({
      editingGame: null,
      promise: dao.games.save(editingGame)
        .then(
          game => onSuccess(`Saved game: "${game.name}"`)
        )
        .catch(onError)
        .finally(this.refreshData)
    });
  };

  startSeed = () => this.setState({ seeding: true });
  stopSeed = () => this.setState({ seeding: false });
  seedBracket = () => {
    const { promise } = this.state;
    const { bracket } = this.props;
    const { dao, onError } = this.context;

    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      seeding: false,
      promise: dao.seedBrackets({ bracketIds: [ bracket.id ], preview: false })
        .then(
          games => this.setState({ games })
        )
        .catch(onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  handleEnterScore = () => {
    const { editingGame, promise } = this.state;
    const { dao, onError, onSuccess } = this.context;

    if (!!promise) {
      promise.cancel();
    }

    this.setState({
      editingGame: null,
      promise: dao.updateGameScore({
        gameId: editingGame.id,
        homeScore: editingGame.sides.home.score,
        visitorScore: editingGame.sides.visitor.score
      })
        .then(() => onSuccess(`Score entered for game: ${editingGame.name}`))
        .catch(onError)
        .finally(this.refreshData)
    });
  };

  changeHomeOnTop = ({ target: { checked: homeOnTop } }) => {
    const { router } = this.context;
    const { location } = this.props;

    router.replace({
      ...location,
      query: {
        ...location.query,
        homeOnTop
      }
    });
  };

  cancelBracketLabelEdit = () => this.setState({ labelingBracket: null });
  handleBracketEdit = ({ game, height }) =>
    this.handleBracketLabelChange({ game, height, bracketLabel: game.bracketLabel || game.name, roundInfo: null });
  handleBracketLabelChange = labelingBracket => this.setState({ labelingBracket });
  saveLabels = () => {
    const { promise, labelingBracket: { game, bracketLabel, roundInfo } } = this.state;
    const { onError, onSuccess, dao } = this.context;

    // don't do anything if we are loading
    if (!!promise) {
      return;
    }

    this.setState({
      // get the one game
      promise: dao.games.getSingle(game.id)
        .then(game => {
          // operate on this bracket only
          const gamesToUpdate = getBracketGames(game);

          return queuePromises(
            _.map(
              gamesToUpdate,
              ({ round, game }) => {
                const ri = roundInfo && roundInfo.length > round ? roundInfo[ round ] : null;
                const prefix = ri ? ri.prefix || '' : '';
                const suffix = ri ? ri.suffix || '' : '';
                const newBracketLabel = `${prefix} ${bracketLabel} ${suffix}`.trim();

                return () =>
                  dao.games.save({
                    ...game,
                    bracketLabel: newBracketLabel.length > 0 ? newBracketLabel : null
                  });
              }
            )
          );
        })
        .then(() => onSuccess('Successfully generated bracket labels!'))
        .catch(onError)
        .finally(this.refreshData)
    });
  };

  handleHoveredTeamIdChange = hoveredTeamId => this.setState({ hoveredTeamId });
  handleOpenGameModal = (action) => {
    if (action.editingScore) {
      this.setState({ editingScore: true, actionModal: false });
    } else {
      this.setState({ actionModal: false });
    }
  };


  render() {
    const {
      games,
      promise,
      filterData,
      editingGame,
      seeding,
      labelingBracket,
      hoveredTeamId,
      editingScore,
      actionModal
    } = this.state;
    const { bracket, location: { query: { homeOnTop } } } = this.props;
    const { model } = this.context;

    if (games === null || filterData === null) {
      if (promise === null) {
        return EmptyState.ERROR;
      } else {
        return EmptyState.LOADING;
      }
    }

    if (games.length === 0) {
      return (<EmptyState>No games are in this bracket</EmptyState>);
    }

    const isHomeOnTop = homeOnTop !== 'false';

    return (
      <LoadingWrapper loading={!!promise}>

        <AdminFeature>
          <div>
            <SeedModal
              homeOnTop={isHomeOnTop} bracketId={bracket.id} open={seeding} onClose={this.stopSeed}
              onSave={this.seedBracket}/>
            <AdminBracketActionModal
              open={actionModal}
              onClose={() => this.setState({ actionModal: false, editingGame: null })}
              onSelect={this.handleOpenGameModal}
            />

            <AdminGameModal
              filterData={filterData} game={editingGame} onChange={this.handleEditGame}
              open={!!editingGame && !editingScore && !actionModal}
              onSave={this.handleSave}
              onClose={this.cancelEdit}/>

            <LabelBracketModal
              value={labelingBracket}
              open={!!labelingBracket}
              onSave={this.saveLabels}
              onChange={this.handleBracketLabelChange}
              onClose={this.cancelBracketLabelEdit}
            />

            <div className="display-flex justify-content-center align-items-center">
              <div className="mg-sm">
                <Button text="Seed Bracket" icon="random" onClick={this.startSeed}/>
              </div>

              <div className="mg-sm">
                <label htmlFor="home-on-top" className="cursor-pointer">Home On Top</label>
                <div>
                  <Toggle id="home-on-top" checked={isHomeOnTop} onChange={this.changeHomeOnTop}/>
                </div>
              </div>
            </div>
            <EnterScoreModal
              game={editingGame}
              onChange={this.handleEditGame}
              open={!!editingScore && !actionModal && !!editingGame}
              onSave={this.handleEnterScore}
              onClose={this.cancelEdit}
            />
          </div>
        </AdminFeature>

        <AdminBracketGenerator
          games={games}
          homeOnTop={isHomeOnTop}
          onBracketEdit={this.handleBracketEdit}
          onClickGame={AdminFeature.canAccess(model) ? this.handleEditGame : null}
          hoveredTeamId={hoveredTeamId} onHoveredTeamIdChange={this.handleHoveredTeamIdChange}
        />
      </LoadingWrapper>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/GameDay/components/pages/AdminBracketPage.jsx